/**
 * Labstep
 *
 * @module report/services/grid-filter
 * @desc Grid Service to inject/remove filter
 */

import type {
  EntityViewParameters,
  PostFilterNodeType,
} from 'labstep-web/models/entity-view.model';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { PostFilterOperator } from 'labstep-web/services/postFilter/constants';
import { cloneDeep } from 'lodash';

export enum PostFilterFieldTypes {
  date = 'date',
  text = 'text',
  numeric = 'numeric',
  boolean = 'boolean',
  options = 'options',
  custom = 'custom',
  metadata = 'metadata',
  entity = 'entity',
}

export const MAPPING_METADATA_TYPE_POST_FIELD: Record<
  MetadataType,
  string | null
> = {
  [MetadataType.default]: 'value',
  [MetadataType.numeric]: 'number',
  [MetadataType.date]: 'date',
  [MetadataType.datetime]: 'date',
  [MetadataType.options]: 'options',
  [MetadataType.molecule]: null,
  [MetadataType.sequence]: null,
  [MetadataType.file]: null,
  [MetadataType.entity_relation]: null,
};

export const MAPPING_METADATA_TYPE_FIELD_TYPE: Record<
  MetadataType,
  PostFilterFieldTypes | null
> = {
  [MetadataType.default]: PostFilterFieldTypes.text,
  [MetadataType.numeric]: PostFilterFieldTypes.numeric,
  [MetadataType.date]: PostFilterFieldTypes.date,
  [MetadataType.datetime]: PostFilterFieldTypes.date,
  [MetadataType.options]: PostFilterFieldTypes.options,
  [MetadataType.molecule]: null,
  [MetadataType.sequence]: null,
  [MetadataType.file]: null,
  [MetadataType.entity_relation]: null,
};

export interface PostFilterSettings {
  fieldType?: PostFilterFieldTypes;
  field?: string;
  path?: string;
  sort?: string;
  multiplicator?: number;
  entityName?: string;
  nullableField?: boolean;
  protocolValueName?: string;
}

export class GridFilterService {
  public static injectUserFilter = (
    node: PostFilterNodeType,
    parameters?: EntityViewParameters | null,
  ): EntityViewParameters => {
    if (
      parameters &&
      parameters.filter &&
      parameters.filter.length === 2
    ) {
      const newParameters = cloneDeep(parameters);
      if (
        newParameters &&
        newParameters.filter &&
        newParameters.filter.length === 2
      ) {
        // Ensure filter[0] has the correct structure
        if (
          !newParameters.filter[0] ||
          !('type' in newParameters.filter[0]) ||
          !('predicates' in newParameters.filter[0])
        ) {
          newParameters.filter[0] = {
            type: PostFilterOperator.and,
            predicates: [],
          };
        }

        // Ensure filter[1] has the correct structure
        if (
          !newParameters.filter[1] ||
          !('type' in newParameters.filter[1]) ||
          !('predicates' in newParameters.filter[1])
        ) {
          newParameters.filter[1] = {
            type: PostFilterOperator.and,
            predicates: [],
          };
        }

        if (
          newParameters.filter[0].predicates &&
          newParameters.filter[0].predicates.length === 0
        ) {
          newParameters.filter[0].predicates.push(node);
        }
      }
      return newParameters;
    }
    return {
      filter: [
        {
          type: PostFilterOperator.and,
          predicates: [node],
        },
        {
          type: PostFilterOperator.and,
          predicates: [],
        },
      ],
    };
  };

  public static injectSystemFilter = (
    node: PostFilterNodeType,
    parameters?: EntityViewParameters | null,
  ): EntityViewParameters => {
    if (
      parameters &&
      parameters.filter &&
      parameters.filter.length === 2
    ) {
      const newParameters = cloneDeep(parameters);
      if (
        newParameters &&
        newParameters.filter &&
        newParameters.filter.length === 2
      ) {
        // Ensure filter[0] has the correct structure
        if (
          !newParameters.filter[0] ||
          !('type' in newParameters.filter[0]) ||
          !('predicates' in newParameters.filter[0])
        ) {
          newParameters.filter[0] = {
            type: PostFilterOperator.and,
            predicates: [],
          };
        }

        // Ensure filter[1] has the correct structure
        if (
          !newParameters.filter[1] ||
          !('type' in newParameters.filter[1]) ||
          !('predicates' in newParameters.filter[1])
        ) {
          newParameters.filter[1] = {
            type: PostFilterOperator.and,
            predicates: [],
          };
        }

        if (newParameters.filter[1].predicates) {
          newParameters.filter[1].predicates.push(node);
        }
      }
      return newParameters;
    }
    return {
      filter: [
        {
          type: PostFilterOperator.and,
          predicates: [],
        },
        {
          type: PostFilterOperator.and,
          predicates: [node],
        },
      ],
    };
  };

  public static removeAllSystemFilters = (
    parameters: EntityViewParameters | null,
  ): EntityViewParameters | null => {
    if (!parameters?.filter || parameters.filter.length !== 2) {
      return parameters;
    }

    const newParameters = cloneDeep(parameters);
    if (!newParameters.filter) {
      return parameters;
    }

    const systemFilter = newParameters.filter[1];

    // Check if legacy format (single PostFilterNodeType)
    if ('path' in systemFilter && !('type' in systemFilter)) {
      // Legacy format: replace with empty new format
      newParameters.filter[1] = {
        type: PostFilterOperator.and,
        predicates: [],
      };
      return newParameters;
    }

    // New format: clear predicates
    if ('type' in systemFilter && 'predicates' in systemFilter) {
      systemFilter.predicates = [];
      return newParameters;
    }

    return parameters;
  };
}
